import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ACTIVETAB, RIGHTSIDECOMPONENTS } from './RightSideComponents';
import { CENTERCOMPONENTS } from './CenterComponents';
import { useMapTabRouteSync } from './useMapTabRouteSync';
import { useSettings } from '../../Settings/SettingsProvider';

interface MapPanelProviderProps {
  children: ReactNode;
}

interface MapPanelContextData {
  activeTab: ACTIVETAB;
  navigateToTab: (tab: ACTIVETAB) => void;
  rightSideComponent: RIGHTSIDECOMPONENTS;
  setRightSideComponent: (component: RIGHTSIDECOMPONENTS) => void;
  centerComponent: CENTERCOMPONENTS;
  setCenterComponent: (component: CENTERCOMPONENTS) => void;
  centerComponentData: any;
  setCenterComponentData: (data: any) => void;
  tabChange: boolean;
  setTabChange: (tabChange: boolean) => void;
  tableExpanded: boolean;
  setTableExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  reportDetailsExpanded: boolean;
  setReportDetailsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarExpanded: boolean;
  setSidebarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  resetMapPanelContext: () => void;
  tableHeight: number;
  setTableHeight: React.Dispatch<React.SetStateAction<number>>;
  loadingCenterComponentData: boolean;
  setLoadingCenterComponentData: React.Dispatch<React.SetStateAction<boolean>>;
  followMmsi: number | null;
  setFollowMmsi: React.Dispatch<React.SetStateAction<number | null>>;
  focusedEventId: string | null;
  setFocusedEventId: React.Dispatch<React.SetStateAction<string | null>>;
}

const MapPanelContext = createContext<MapPanelContextData | undefined>(
  undefined,
);

export const MapPanelProvider = ({ children }: MapPanelProviderProps) => {
  const { activeTab, navigateToTab } = useMapTabRouteSync();
  const { settings } = useSettings();
  const [rightSideComponent, setRightSideComponent] =
    useState<RIGHTSIDECOMPONENTS>(RIGHTSIDECOMPONENTS.NONE);
  const [centerComponent, setCenterComponent] = useState<CENTERCOMPONENTS>(
    CENTERCOMPONENTS.NONE,
  );
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [centerComponentData, setCenterComponentData] = useState(null);
  const [loadingCenterComponentData, setLoadingCenterComponentData] =
    useState(false);
  const [tabChange, setTabChange] = useState<boolean>(false);
  const [tableExpanded, setTableExpanded] = useState<boolean>(true);
  const [reportDetailsExpanded, setReportDetailsExpanded] =
    useState<boolean>(true);
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(
    settings['ribbonPlugin.settings']?.is_sidebar_expanded ?? true,
  );
  const [followMmsi, setFollowMmsi] = useState<number | null>(null);
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);

  const resetMapPanelContext: () => void = useCallback(() => {
    navigateToTab(ACTIVETAB.NONE);
    setRightSideComponent(RIGHTSIDECOMPONENTS.NONE);
    setCenterComponent(CENTERCOMPONENTS.NONE);
    setCenterComponentData(null);
    setTabChange(false);
    setTableExpanded(true);
    setReportDetailsExpanded(true);
    setSidebarExpanded(
      settings['ribbonPlugin.settings']?.is_sidebar_expanded ?? true,
    );
    setFollowMmsi(null);
    setFocusedEventId(null);
  }, [
    navigateToTab,
    setRightSideComponent,
    setCenterComponent,
    setCenterComponentData,
    setTabChange,
    setTableExpanded,
    setReportDetailsExpanded,
    setSidebarExpanded,
    settings,
  ]);

  const contextValue = useMemo(
    () => ({
      activeTab,
      navigateToTab,
      rightSideComponent,
      setRightSideComponent,
      centerComponent,
      setCenterComponent,
      centerComponentData,
      setCenterComponentData,
      tabChange,
      setTabChange,
      tableExpanded,
      setTableExpanded,
      reportDetailsExpanded,
      setReportDetailsExpanded,
      sidebarExpanded,
      setSidebarExpanded,
      resetMapPanelContext,
      tableHeight,
      setTableHeight,
      loadingCenterComponentData,
      setLoadingCenterComponentData,
      followMmsi,
      setFollowMmsi,
      focusedEventId,
      setFocusedEventId,
    }),
    [
      activeTab,
      navigateToTab,
      rightSideComponent,
      setRightSideComponent,
      centerComponent,
      setCenterComponent,
      centerComponentData,
      setCenterComponentData,
      tabChange,
      setTabChange,
      tableExpanded,
      setTableExpanded,
      reportDetailsExpanded,
      setReportDetailsExpanded,
      sidebarExpanded,
      setSidebarExpanded,
      resetMapPanelContext,
      tableHeight,
      setTableHeight,
      loadingCenterComponentData,
      setLoadingCenterComponentData,
      followMmsi,
      setFollowMmsi,
      focusedEventId,
      setFocusedEventId,
    ],
  );

  return (
    <MapPanelContext.Provider value={contextValue}>
      {children}
    </MapPanelContext.Provider>
  );
};

export const useMapPanelContext = () => {
  const context = useContext(MapPanelContext);
  if (context === undefined) {
    throw new Error(
      'useMapPanelContext must be used within a MapPanelContextProvider',
    );
  }
  return context;
};
